import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AsyncStatus,
  Customer,
  Dashboard,
  Recipient,
  State,
  Pagination,
  Sort,
  StateFamily,
} from '@algoan/dashboard/model';
import { allTeamsValue } from '@algoan/dashboard/env';
import { updateAuthInformation } from './auth-slice';

export type CustomerListLoadingError = 'noCustomerFound' | 'genericError' | undefined;

export type CustomerSort = Sort<'dashboard.createdAt'>;

export interface CustomerReducerState {
  customerList: Customer[];
  customerListError: CustomerListLoadingError;
  customerListFetchingStatus: AsyncStatus;
  customers: { [key: string]: Customer };
  pagination: Pagination;
  sort: CustomerSort;
  filter: CustomersFilter;
  customerFetchingStatus: AsyncStatus;
}

export interface CustomersFilter {
  hasAnalysis?: boolean;
  states: State[];
  team: string;
  inputValue?: string;
  customersFilterFetchingStatus: AsyncStatus;
  project?: { id: string; name: string };
  stateFamilies?: StateFamily[];
}

export const initialCustomerFilter: CustomersFilter = {
  hasAnalysis: undefined,
  states: [],
  customersFilterFetchingStatus: AsyncStatus.IDLE,
  team: allTeamsValue,
  inputValue: undefined,
  project: undefined,
};

const initialPagination = { currentPage: 0 };

const getInitialState = (): CustomerReducerState => ({
  customerList: [],
  customerListFetchingStatus: AsyncStatus.IDLE,
  customerListError: undefined,
  customers: {},
  pagination: initialPagination,
  sort: { sortBy: 'dashboard.createdAt', sortDirection: 'DESC' },
  filter: initialCustomerFilter,
  customerFetchingStatus: AsyncStatus.IDLE,
});

const initialState = getInitialState();

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    fetchNextCustomerStart(state) {
      state.customerListFetchingStatus = AsyncStatus.PENDING;
      state.customerListError = undefined;
    },
    fetchNextCustomerFailure(
      state,
      action: PayloadAction<{ error: CustomerListLoadingError; paginationData?: Pagination }>,
    ) {
      const { error, paginationData } = action.payload;
      state.customerListFetchingStatus = AsyncStatus.REJECTED;
      state.customerListError = error;
      state.pagination = { ...state.pagination, ...paginationData };

      if (error === 'noCustomerFound') {
        state.customerList = [];
      }
    },
    fetchNextCustomerSuccess(state, action: PayloadAction<{ nextCustomers: Customer[]; paginationData: Pagination }>) {
      const { nextCustomers, paginationData } = action.payload;

      state.customers = {
        ...state.customers,
        ...nextCustomers.reduce<{ [key: string]: Customer }>((customers, customer) => {
          customers[customer.id] = customer;

          return customers;
        }, {}),
      };

      if (state.pagination.currentPage === 0) {
        state.customerList = nextCustomers;
      } else {
        state.customerList.push(...nextCustomers);
      }

      state.customerListFetchingStatus = AsyncStatus.RESOLVED;
      state.customerListError = undefined;
      state.pagination = { ...state.pagination, ...paginationData };
    },
    fetchOneCustomerFailure(state) {
      state.customerFetchingStatus = AsyncStatus.REJECTED;
    },
    fetchOneCustomerStart(state) {
      state.customerFetchingStatus = AsyncStatus.PENDING;
    },
    fetchOneCustomerSuccess(state, action: PayloadAction<Customer>) {
      state.customerFetchingStatus = AsyncStatus.RESOLVED;
      state.customers[action.payload.id] = action.payload;
    },
    resetCustomerList(state) {
      state.customerList = [];
      state.customerListError = undefined;
      state.customerListFetchingStatus = AsyncStatus.IDLE;
      state.pagination = initialPagination;
    },
    resetState(state) {
      state.pagination = initialPagination;
      state.customerFetchingStatus = AsyncStatus.IDLE;
      state.customerList = [];
      state.customerListError = undefined;
      state.customerListFetchingStatus = AsyncStatus.IDLE;
      state.customers = {};
    },
    sortCustomersStart(state) {
      state.sort.sortDirection = state.sort.sortDirection === 'ASC' ? 'DESC' : 'ASC';
      state.sort.sortLoading = 'dashboard.createdAt';
      state.pagination = initialPagination;
    },
    sortCustomersSuccess(state) {
      state.sort.sortLoading = undefined;
    },
    filterCustomersStart(state, action: PayloadAction<Partial<Omit<CustomersFilter, 'isLoading'>>>) {
      state.filter = { ...state.filter, ...action.payload, customersFilterFetchingStatus: AsyncStatus.PENDING };
      state.pagination = initialPagination;
    },
    filterCustomersSuccess(state) {
      state.filter.customersFilterFetchingStatus = AsyncStatus.IDLE;
    },
    updateCustomerDashboardState(state, action: PayloadAction<{ customerId: string; newDashboard: Dashboard }>) {
      state.customers[action.payload.customerId].dashboard = action.payload.newDashboard;
    },
    updateCustomerShares(state, action: PayloadAction<{ customerId: string; recipients: Recipient[] }>) {
      const { customerId, recipients } = action.payload;
      const customerRecipients = recipients.map(({ projectId, organizationId }) => {
        return {
          sharedAt: new Date().toString(),
          projectId,
          organizationId,
        };
      });
      const currentCustomerRecipients = state.customers[customerId]?.shares;

      state.customers[customerId].shares = [...(currentCustomerRecipients ?? []), ...customerRecipients];
    },
    shareFailure(state, action: PayloadAction<{ customerId: string; recipients: Recipient[] }>) {
      const { customerId } = action.payload;

      action.payload.recipients.forEach((recipient) => {
        const shares = state.customers[customerId]?.shares;
        if (shares !== undefined) {
          shares.splice(shares.map((share) => share.projectId).indexOf(recipient.projectId), 1);
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateAuthInformation, (state, action) => {
      if (typeof action.payload === 'undefined') {
        // we clear the state on logout
        return getInitialState();
      }

      return state;
    });
  },
});

export const { reducer: customerReducer } = customerSlice;
export const {
  fetchNextCustomerFailure,
  fetchNextCustomerStart,
  fetchNextCustomerSuccess,
  fetchOneCustomerFailure,
  fetchOneCustomerStart,
  fetchOneCustomerSuccess,
  filterCustomersStart,
  resetCustomerList,
  resetState,
  sortCustomersStart,
  sortCustomersSuccess,
  filterCustomersSuccess,
  updateCustomerDashboardState,
  updateCustomerShares,
  shareFailure,
} = customerSlice.actions;
